import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, DELETE } from 'helpers/api'
import { getTableSelectProps } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'

// Селекты встроены в ячейки: рамку и отступы даёт таблица, а не контрол
const SELECT_PORTAL_PROPS = getTableSelectProps()

// Расписание витрины точки: какое меню и по каким ценам работает в выбранный период.
// Правило без периода — меню точки по умолчанию, оно редактируется селектом выше.
function MenuSchedule({ posId }) {
	const { t } = useTranslation()

	const [schedules, setSchedules] = useState([])
	const [menus, setMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])
	const [pricePeriods, setPricePeriods] = useState([])
	const [newRule, setNewRule] = useState({
		external_menu_id: null,
		price_category_id: null,
		price_period_id: null,
		priority: 0,
		is_pos_applied: false,
	})

	async function getSchedules() {
		if (!posId) return
		const response = await GET('/menu-schedules', { pos_id: posId })
		if (httpOk(response)) {
			setSchedules((response.data?.data ?? []).filter(item => item.price_period_id))
		}
	}

	async function getDictionaries() {
		const menusResponse = await GET('/external-groups', { size: 100 })
		if (httpOk(menusResponse)) setMenus(menusResponse.data?.data ?? [])

		const categoriesResponse = await GET('/price-categories')
		if (httpOk(categoriesResponse)) setPriceCategories(categoriesResponse.data?.data ?? [])

		const periodsResponse = await GET('/price-periods')
		if (httpOk(periodsResponse)) setPricePeriods(periodsResponse.data?.data ?? [])
	}

	async function createRule() {
		if (!newRule.price_period_id) return

		const response = await POST('/menu-schedules', {
			pos_id: posId,
			external_menu_id: newRule.external_menu_id,
			price_category_id: newRule.price_category_id,
			price_period_id: newRule.price_period_id,
			priority: Number(newRule.priority) || 0,
			is_pos_applied: newRule.is_pos_applied,
		}, { loader: true })

		if (httpOk(response)) {
			setNewRule({ external_menu_id: null, price_category_id: null, price_period_id: null, priority: 0, is_pos_applied: false })
			getSchedules()
		}
	}

	async function deleteRule(id) {
		const response = await DELETE(`/menu-schedules/${id}`, {}, { loader: true })
		if (httpOk(response)) getSchedules()
	}

	useEffect(() => {
		getSchedules()
		getDictionaries()
	}, [posId]) // eslint-disable-line react-hooks/exhaustive-deps

	if (!posId) return null

	return (
		<div className="form-group">
			<h5 className="fw-600">
				{t('menu_schedule')}
				<HintIcon text={`${t('menu_schedule_hint')}\n${t('apply_on_pos_hint')}`} />
			</h5>

			<div className="table-responsive mt-2">
				<table className="table table-bordered mb-0">
					<thead>
						<tr>
							<th>{t('price_period')}</th>
							<th>{t('menu')}</th>
							<th>{t('price_category')}</th>
							<th style={{ width: '90px' }}>{t('priority')}</th>
							<th style={{ width: '90px' }} className="text-center">{t('apply_on_pos')}</th>
							<th style={{ width: '50px' }}></th>
						</tr>
					</thead>
					<tbody>
						{schedules.map((item) => (
							<tr key={item.id}>
								<td className="vertical-middle">{item.price_period_name}</td>
								<td className="vertical-middle">{item.external_menu_name ?? t('pos_default_menu_main')}</td>
								<td className="vertical-middle">{item.price_category_name ?? t('base_price')}</td>
								<td className="vertical-middle">{item.priority}</td>
								<td className="vertical-middle text-center">
									{item.is_pos_applied
										? <i className="uil uil-check text-success"></i>
										: <i className="uil uil-minus text-muted"></i>}
								</td>
								<td className="vertical-middle text-center">
									<div className="table-action-button table-action-danger-button"
										onClick={() => deleteRule(item.id)}>
										<i className="uil uil-trash-alt"></i>
									</div>
								</td>
							</tr>
						))}
						<tr>
							<td className="td-select">
								<Select
									{...SELECT_PORTAL_PROPS}
									isClearable
									options={pricePeriods}
									value={pricePeriods.find(option => option.id === newRule.price_period_id) || ''}
									onChange={(option) => setNewRule({ ...newRule, 'price_period_id': option ? option.id : null })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id} />
							</td>
							<td className="td-select">
								<Select
									{...SELECT_PORTAL_PROPS}
									isClearable
									options={menus}
									value={menus.find(option => option.id === newRule.external_menu_id) || ''}
									onChange={(option) => setNewRule({ ...newRule, 'external_menu_id': option ? option.id : null })}
									placeholder={t('pos_default_menu_main')}
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id} />
							</td>
							<td className="td-select">
								<Select
									{...SELECT_PORTAL_PROPS}
									isClearable
									options={priceCategories}
									value={priceCategories.find(option => option.id === newRule.price_category_id) || ''}
									onChange={(option) => setNewRule({ ...newRule, 'price_category_id': option ? option.id : null })}
									placeholder={t('base_price')}
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id} />
							</td>
							<td className="td-input">
								<input type="number" className="form-control"
									value={newRule.priority}
									onChange={(e) => setNewRule({ ...newRule, 'priority': e.target.value })} />
							</td>
							<td className="text-center vertical-middle">
								<div className="form-check form-switch form-switch-sm d-inline-block">
									<input type="checkbox" className="form-check-input"
										checked={!!newRule.is_pos_applied}
										onChange={(e) => setNewRule({ ...newRule, 'is_pos_applied': e.target.checked })} />
								</div>
							</td>
							<td className="text-center vertical-middle">
								<div className="table-action-button table-action-primary-button"
									onClick={createRule}>
									<i className="uil uil-plus"></i>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default MenuSchedule
